import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Select,
  Table,
  Typography,
  Row,
  Col,
  message,
  Tag,
  Statistic,
} from 'antd';
import {
  ThunderboltOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { apiGet } from '../services/api';

const { Title, Text } = Typography;

const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  cardBg: '#ffffff',
  borderDark: '#f0f0f0',
};

// PageHeader 组件
const PageHeader: React.FC<{ title: string; icon: React.ReactNode }> = ({ title, icon }) => (
  <div style={{ marginBottom: 24 }}>
    <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ color: COLORS.primary }}>{icon}</span>
      {title}
    </Title>
    <div style={{
      width: 60,
      height: 4,
      background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
      borderRadius: 2,
      marginTop: 8,
    }} />
  </div>
);

// SimpleCard 组件
const SimpleCard: React.FC<{ children: React.ReactNode; title?: string; extra?: React.ReactNode; style?: React.CSSProperties }> = ({
  children, title, extra, style
}) => (
  <Card
    title={title}
    extra={extra}
    style={{
      borderRadius: 8,
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      ...style,
    }}
    styles={{
      header: { borderBottom: `1px solid ${COLORS.borderDark}` },
    }}
  >
    {children}
  </Card>
);

// CopyableCell 组件 - 点击复制内容到剪贴板
const CopyableCell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    const text = e.currentTarget.innerText;
    if (text) {
      navigator.clipboard.writeText(text).then(() => {
        message.success('已复制');
      }).catch(() => {
        // silent
      });
    }
  };

  return (
    <span onClick={handleClick} style={{ cursor: 'pointer' }}>
      {children}
    </span>
  );
};

interface ActiveAtmosphereTask {
  taskId: string;
  roomId: number;
  roomName: string;
  streamerUserId: number;
  streamerName: string;
  roomStartTime: string | null;
  roomEndTime: string | null;
  initialBotCount: number;
  joinedCount: number;
  joinFailedCount: number;
  leftCount: number;
  sentSuccessCount: number;
  sentFailedCount: number;
  followSuccessCount: number;
  activeUserCount: number;
  totalConnectionCount: number;
  peakActiveUserCount: number;
  peakTotalConnectionCount: number;
  status: string;
  errorMessage: string | null;
  createTime: string;
  startTime: string | null;
  endTime: string | null;
}

interface Summary {
  total: number;
  active: number;
  completed: number;
  failed: number;
}

const statusMap: Record<string, { color: string; text: string }> = {
  pending:   { color: 'default',    text: '等待中' },
  running:   { color: 'processing', text: '运行中' },
  completed: { color: 'success',    text: '已完成' },
  failed:    { color: 'error',      text: '失败' },
};

const LiveRoomActiveAtmosphere: React.FC = () => {
  const [tasks, setTasks] = useState<ActiveAtmosphereTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<Summary>({ total: 0, active: 0, completed: 0, failed: 0 });
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 获取任务列表
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      let path = `/api/active-atmosphere/tasks?page=${page}&size=${pageSize}`;
      if (statusFilter) {
        path += `&status=${statusFilter}`;
      }
      const response = await apiGet<any>(path);
      if (response.code === 0 && response.data) {
        setTasks(response.data.list || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter]);

  // 获取统计
  const fetchSummary = useCallback(async () => {
    try {
      const response = await apiGet<any>('/api/active-atmosphere/summary');
      if (response.code === 0 && response.data) {
        setSummary(response.data);
      }
    } catch (error) {
      // silent
    }
  }, []);

  // 初始加载 + 自动刷新
  useEffect(() => {
    fetchTasks();
    fetchSummary();

    timerRef.current = setInterval(() => {
      fetchTasks();
      fetchSummary();
    }, 30000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [fetchTasks, fetchSummary]);

  const columns = [
    {
      title: '房间',
      dataIndex: 'roomName',
      width: 140,
      ellipsis: true,
      render: (name: string, record: ActiveAtmosphereTask) => (
        <CopyableCell><Text>{name || `房间 ${record.roomId}`}</Text></CopyableCell>
      ),
    },
    {
      title: '房间ID',
      dataIndex: 'roomId',
      width: 90,
      align: 'center' as const,
      render: (id: number) => <CopyableCell><Text>{id}</Text></CopyableCell>,
    },
    {
      title: '主播',
      dataIndex: 'streamerName',
      width: 100,
      ellipsis: true,
      render: (name: string) => (
        <CopyableCell><Text>{name}</Text></CopyableCell>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (status: string) => {
        const s = statusMap[status] || { color: 'default', text: status };
        return <CopyableCell><Tag color={s.color}>{s.text}</Tag></CopyableCell>;
      },
    },
    {
      title: '开播时间',
      dataIndex: 'roomStartTime',
      width: 155,
      render: (time: string) => <CopyableCell><Text>{time || '-'}</Text></CopyableCell>,
    },
    {
      title: '结束时间',
      dataIndex: 'roomEndTime',
      width: 155,
      render: (time: string) => <CopyableCell><Text>{time || '-'}</Text></CopyableCell>,
    },
    {
      title: '实时真人',
      dataIndex: 'activeUserCount',
      width: 80,
      align: 'center' as const,
      render: (count: number) => <CopyableCell><Text>{count}</Text></CopyableCell>,
    },
    {
      title: '实时总人数',
      dataIndex: 'totalConnectionCount',
      width: 90,
      align: 'center' as const,
      render: (count: number) => <CopyableCell><Text>{count}</Text></CopyableCell>,
    },
    {
      title: '峰值真人',
      dataIndex: 'peakActiveUserCount',
      width: 80,
      align: 'center' as const,
      render: (count: number) => <CopyableCell><Text>{count}</Text></CopyableCell>,
    },
    {
      title: '峰值总人数',
      dataIndex: 'peakTotalConnectionCount',
      width: 90,
      align: 'center' as const,
      render: (count: number) => <CopyableCell><Text>{count}</Text></CopyableCell>,
    },
    {
      title: '初始机器人',
      dataIndex: 'initialBotCount',
      width: 90,
      align: 'center' as const,
      render: (count: number) => <CopyableCell><Text>{count}</Text></CopyableCell>,
    },
    {
      title: '已加入',
      dataIndex: 'joinedCount',
      width: 80,
      align: 'center' as const,
      render: (joined: number, record: ActiveAtmosphereTask) => (
        <CopyableCell><Text>{joined}</Text></CopyableCell>
      ),
    },
    {
      title: '已退出',
      dataIndex: 'leftCount',
      width: 70,
      align: 'center' as const,
      render: (count: number) => <CopyableCell><Text>{count}</Text></CopyableCell>,
    },
    {
      title: '加入失败',
      dataIndex: 'joinFailedCount',
      width: 80,
      align: 'center' as const,
      render: (count: number) => (
        <CopyableCell>
          <Text style={count > 0 ? { color: COLORS.error } : undefined}>{count}</Text>
        </CopyableCell>
      ),
    },
    {
      title: '消息成功',
      dataIndex: 'sentSuccessCount',
      width: 80,
      align: 'center' as const,
      render: (count: number) => <CopyableCell><Text>{count}</Text></CopyableCell>,
    },
    {
      title: '消息失败',
      dataIndex: 'sentFailedCount',
      width: 80,
      align: 'center' as const,
      render: (count: number) => (
        <CopyableCell>
          <Text style={count > 0 ? { color: COLORS.error } : undefined}>{count}</Text>
        </CopyableCell>
      ),
    },
    {
      title: '关注成功',
      dataIndex: 'followSuccessCount',
      width: 80,
      align: 'center' as const,
      render: (count: number) => <CopyableCell><Text>{count}</Text></CopyableCell>,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 155,
      render: (time: string) => <CopyableCell><Text>{time}</Text></CopyableCell>,
    },
  ];

  return (
    <div style={{ padding: '0 0 24px' }}>
      <PageHeader title="动态直播间气氛" icon={<ThunderboltOutlined />} />

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic title="总任务" value={summary.total} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="活跃" value={summary.active} valueStyle={{ color: COLORS.primary }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="已完成" value={summary.completed} valueStyle={{ color: COLORS.success }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="失败" value={summary.failed} valueStyle={summary.failed > 0 ? { color: COLORS.error } : undefined} />
          </Card>
        </Col>
      </Row>

      {/* 任务列表 */}
      <SimpleCard
        title="任务列表"
        extra={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Select
              allowClear
              placeholder="状态筛选"
              style={{ width: 120 }}
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
              options={[
                { value: 'pending', label: '等待中' },
                { value: 'running', label: '运行中' },
                { value: 'completed', label: '已完成' },
                { value: 'failed', label: '失败' },
              ]}
            />
            <Button
              icon={<SyncOutlined />}
              onClick={() => { fetchTasks(); fetchSummary(); }}
              loading={loading}
            >
              刷新
            </Button>
          </div>
        }
      >
        <Table
          dataSource={tasks}
          columns={columns}
          rowKey="taskId"
          loading={loading}
          scroll={{ x: 1800 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p) => setPage(p),
          }}
          size="middle"
        />
      </SimpleCard>
    </div>
  );
};

export default LiveRoomActiveAtmosphere;
